// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Non-blocking banner shown when the vault is locked but cloud storage
 * is connected. This happens when:
 *   - Redis restarts (all vault keys lost)
 *   - The vault TTL expires (user idle >60 min without any requests)
 *   - User opens the app from a stale session (refresh token still valid
 *     but no password available to derive the vault key)
 *   - User reconnects storage in a session where the vault was never unlocked
 *
 * The banner appears at the top of the content area and lets the user
 * re-unlock the vault without a full logout/login cycle:
 *   - Password users: enter their password inline
 *   - OIDC users with a vault passphrase: enter their passphrase inline
 *   - OIDC users without a passphrase: directed to set one up
 *
 * Normal CRUD operations still work — the banner is informational.
 * Only cloud sync is paused while the vault is locked.
 *
 * Detection: instead of polling, the banner reads the initial sync status
 * from useSyncStatus (fetched once at login for cloud users). If a user
 * hits a 412 (vault locked) during normal usage, the sync status query
 * is invalidated which causes this banner to re-evaluate.
 */

import { useState, useCallback, useEffect } from 'react';
import { KeyRound, Loader2, X, ShieldAlert } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { PasswordInput } from '@/app/components/password-input';
import { useAuthStore } from '@/app/stores/auth-store';
import { useSyncStatus, useUnlockVault } from '@/app/features/storage';
import { deriveVaultKey, bytesToBase64 } from '@/lib/crypto';
import { VaultPassphraseDialog } from './vault-passphrase-dialog';

export function VaultRelockBanner() {
  const { user } = useAuthStore();
  const { data: syncStatus } = useSyncStatus();
  const unlockMutation = useUnlockVault();

  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [deriving, setDeriving] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [passphraseDialogOpen, setPassphraseDialogOpen] = useState(false);

  // Reset dismiss + form state when vault becomes unlocked (e.g. after
  // successful re-unlock or fresh login).
  useEffect(() => {
    if (syncStatus?.vault_unlocked) {
      setDismissed(false);
      setSecret('');
      setError('');
    }
  }, [syncStatus?.vault_unlocked]);

  // Use auth store for storage provider check (available immediately, no fetch).
  const isCloudUser =
    user?.storageProvider === 'gdrive' ||
    user?.storageProvider === 'dropbox';

  const vaultLocked = syncStatus?.vault_unlocked === false;
  const hasPassphrase = syncStatus?.has_vault_passphrase === true;
  const hasPassword = user?.hasPassword === true;

  // Show banner when: cloud user, vault is locked, user hasn't dismissed.
  const shouldShow = isCloudUser && vaultLocked && !dismissed;

  const isPending = deriving || unlockMutation.isPending;

  const handleReUnlock = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!user || !secret.trim()) return;

      setError('');

      try {
        setDeriving(true);
        const keyBytes = await deriveVaultKey(secret, user.id);
        const keyBase64 = bytesToBase64(keyBytes);
        setDeriving(false);

        await unlockMutation.mutateAsync(keyBase64);

        // Success — state is reset by the useEffect above when vault_unlocked flips.
      } catch (err) {
        setDeriving(false);
        setError(
          err instanceof Error ? err.message : 'Failed to unlock vault.',
        );
      }
    },
    [user, secret, unlockMutation],
  );

  // Pure OIDC user without a passphrase — they need to set one up first.
  // Hybrid users (has password + OIDC) should just enter their password.
  const needsPassphraseSetup = isCloudUser && vaultLocked && !hasPassphrase && !hasPassword;

  if (!shouldShow) return null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
              <ShieldAlert className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-foreground">
                Cloud sync paused
              </p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">
                {needsPassphraseSetup
                  ? 'Set up a vault passphrase to encrypt and sync your data to the cloud.'
                  : hasPassphrase
                    ? 'Enter your vault passphrase to resume encrypted cloud sync.'
                    : 'Enter your account password to resume encrypted cloud sync.'}
              </p>

              {needsPassphraseSetup ? (
                /* OIDC user without passphrase — button to open setup dialog */
                <button
                  onClick={() => setPassphraseDialogOpen(true)}
                  className="mt-2.5 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <KeyRound className="h-3 w-3" />
                  Set Up Passphrase
                </button>
              ) : (
                /* Password user or OIDC user with passphrase — inline unlock form */
                <form
                  onSubmit={handleReUnlock}
                  className="mt-2.5 flex items-center gap-2"
                >
                  <PasswordInput
                    reveals={hasPassphrase ? 'vault passphrase' : 'password'}
                    value={secret}
                    onChange={(e) => {
                      setSecret(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder={
                      hasPassphrase ? 'Vault passphrase' : 'Password'
                    }
                    disabled={isPending}
                    autoComplete="current-password"
                    wrapperClassName="w-full max-w-[240px]"
                    className="h-8 w-full rounded-md border border-border bg-background px-2.5 text-[12px] text-foreground placeholder:text-muted-foreground/60 transition-colors focus:border-primary focus:outline-none disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={isPending || !secret.trim()}
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 text-[12px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {deriving ? 'Deriving...' : 'Unlocking...'}
                      </>
                    ) : (
                      <>
                        <KeyRound className="h-3 w-3" />
                        Unlock
                      </>
                    )}
                  </button>
                </form>
              )}

              {error && (
                <p className="mt-1.5 text-[11px] text-destructive">{error}</p>
              )}
            </div>

            {/* Dismiss button */}
            <button
              onClick={() => setDismissed(true)}
              className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Passphrase setup dialog for OIDC users */}
      <VaultPassphraseDialog
        open={passphraseDialogOpen}
        onOpenChange={setPassphraseDialogOpen}
        mode="setup"
      />
    </>
  );
}
